import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
	SaveUserSettingsCommandStringBoolean as SaveUserVisibilitySettingsCommand,
	useProductCenterApi,
} from 'api';
import {
	userSettingsKeys,
	userVisibilitySettingsQuery,
} from 'api/queries/UserSettingsQueries';
import { SettingsCategory } from 'api/v1/data-contracts';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';

export interface PdfUserSettingsCategories {
	dealer: SettingsCategory;
	customer: SettingsCategory;
}

interface DealerCustomerSettings<T> {
	forDealer?: T;
	forCustomer?: T;
}

/**
 * Generic loader for per-tab (dealer/customer) PDF print user settings. Shared
 * by PDF print dialogs - the feature provides the categories and a mapper from
 * the stored record to its typed settings.
 */
export const useGetPdfUserSettings = <T>(
	categories: PdfUserSettingsCategories,
	mapRecordToSettings: (dict?: Record<string, boolean>) => T | undefined
): { isLoading: boolean; settings: DealerCustomerSettings<T> } => {
	const { api } = useProductCenterApi();

	const { data: dataCustomer, isLoading: isLoadingCustomer } = useQuery({
		...userVisibilitySettingsQuery(api, categories.customer),
		throwOnError: false,
		staleTime: 300000, // 5 minutes
		gcTime: 0,
	});

	const { data: dataDealer, isLoading: isLoadingDealer } = useQuery({
		...userVisibilitySettingsQuery(api, categories.dealer),
		throwOnError: false,
		staleTime: 300000, // 5 minutes
		gcTime: 0,
	});

	return {
		isLoading: isLoadingCustomer || isLoadingDealer,
		settings: {
			forCustomer: mapRecordToSettings(dataCustomer?.data.settings),
			forDealer: mapRecordToSettings(dataDealer?.data.settings),
		},
	};
};

/**
 * Generic store hook for per-tab (dealer/customer) PDF print user settings.
 * Persisting is best-effort - failures are swallowed because this is not a
 * critical feature.
 */
export const useStorePdfUserSettings = (
	categories: PdfUserSettingsCategories
) => {
	const { api } = useProductCenterApi();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();
	const queryClient = useQueryClient();

	const saveSettingsMutation = useMutation<
		string,
		unknown,
		SaveUserVisibilitySettingsCommand
	>({
		mutationFn: async (data) => {
			return (await api.setUserVisibilitySettings(data)).data;
		},
	});

	const convertToRecord = (userSettings: object): Record<string, boolean> => {
		return Object.entries(userSettings).reduce(
			(acc, [key, value]) => {
				if (typeof value === 'boolean') {
					acc[key] = value;
				}
				return acc;
			},
			{} as Record<string, boolean>
		);
	};

	const updatePdfSettings = async (
		formModel: object,
		isForDealer: boolean
	) => {
		const category = isForDealer ? categories.dealer : categories.customer;

		try {
			saveSettingsMutation.mutate({
				settings: convertToRecord(formModel),
				category,
			});

			await queryClient.invalidateQueries({
				queryKey: userSettingsKeys.settings(category),
			});
		} catch (postError) {
			// we don't want to issue an error for the user, because this is not
			// a critical feature
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			checkConsoleDebuggingReadAccess() && console.error(postError);
		}
	};

	return updatePdfSettings;
};
